import { defineStore } from "pinia";
import type { AxiosRequestConfig } from "axios";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { StoreModule } from "@/constants/store";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import {
  BackupJobDirection,
  WorkloadHealthStatus,
  type BackupAndRestoreWidgetData,
  type BackupConnector,
  type BackupJob,
} from "@/constants/cloud-backup.ts";
import { isWorkloadConnected } from "@/_helpers/cloud-backup.ts";
import type { DataValue, Pagination } from "@/types";

export interface BackupConnectionStateResponse {
  enabled: boolean;
}

export interface BackupJobsResponse {
  items: BackupJob[];
  total: number;
}

/** Server-paged job rows for one direction. The two directions never share state. */
export interface BackupJobsState {
  items: BackupJob[];
  total: number;
  pagination: Pagination;
  loading: boolean;
}

export interface CloudBackupState {
  /**
   * Whether Cloud Backup is activated for this workspace. GET /backupAndRestore/status
   * answers with a plain boolean for the workspace view — unlike the MSP endpoint of the
   * same name, there is no PROVISIONING or PARENT_NOT_ACTIVATED to distinguish here.
   */
  enabled: boolean;
  connectors: BackupConnector[];
  connectorsLoading: boolean;
  jobs: Record<BackupJobDirection, BackupJobsState>;
  dashboard: DataValue<BackupAndRestoreWidgetData>;
}

const defaultJobsState = (): BackupJobsState => ({
  items: [],
  total: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  loading: false,
});

export const defaultCloudBackupState: CloudBackupState = {
  enabled: false,
  connectors: [],
  connectorsLoading: false,
  jobs: {
    [BackupJobDirection.BACKUP]: defaultJobsState(),
    [BackupJobDirection.RESTORE]: defaultJobsState(),
  },
  dashboard: { loading: false },
};

// Each job tab persists its own filters, so the two directions cannot narrow each other.
// Exported so the jobs list reads its filter context from the same map the fetch uses.
export const JOB_FILTER_CONTEXTS: Record<
  BackupJobDirection,
  FilterContext.BACKUP_AND_RESTORE_BACKUP_JOBS | FilterContext.BACKUP_AND_RESTORE_RESTORE_JOBS
> = {
  [BackupJobDirection.BACKUP]: FilterContext.BACKUP_AND_RESTORE_BACKUP_JOBS,
  [BackupJobDirection.RESTORE]: FilterContext.BACKUP_AND_RESTORE_RESTORE_JOBS,
};

// Health values that put a connected cloud app on the "needing attention" tile.
const HEALTH_NEEDING_ATTENTION: WorkloadHealthStatus[] = [
  WorkloadHealthStatus.UNHEALTHY,
  WorkloadHealthStatus.CRITICAL,
];

/**
 * Every Cloud Backup endpoint the workspace (non-MSP) control panel talks to.
 *
 * Deliberately separate from `msp-cloud-backup.module`, which serves the /msp/* endpoints
 * and the channel-level views. Keepit SSO is the one exception: the new page reuses
 * `useMspCloudBackupStore().autologin()` rather than duplicating the same call here.
 */
export const useCloudBackupStore = defineStore(StoreModule.CLOUD_BACKUP, {
  persist: false,
  // Deep-cloned so the nested per-direction objects on the defaults are never shared with
  // live state, which an in-place mutation would otherwise write into.
  state: (): CloudBackupState => cloneDeep(defaultCloudBackupState),
  getters: {
    /**
     * Users backed up across every cloud app. The endpoint is unpaged, so summing the
     * whole array is the true total rather than a page of it.
     */
    backedUpUsers(state): number {
      return state.connectors.reduce((sum, connector) => sum + connector.usage, 0);
    },
    seatLimit(state): number {
      return state.connectors.reduce((sum, connector) => sum + connector.limit, 0);
    },
    /**
     * Cloud apps an admin has to do something about: one that is not connected backs
     * nothing up at all, so it needs attention whatever health the backend last recorded
     * for it. A connected app qualifies on its health alone.
     */
    connectorsNeedingAttention(state): number {
      return state.connectors.filter(
        (connector) =>
          !isWorkloadConnected(connector) ||
          HEALTH_NEEDING_ATTENTION.includes(connector.healthStatus)
      ).length;
    },
    /** Cloud apps that are not connected at all, which is what the warning banner counts. */
    disconnectedConnectors(state): BackupConnector[] {
      return state.connectors.filter((connector) => !isWorkloadConnected(connector));
    },
  },
  actions: {
    async getConnectionState() {
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreSingleViewConnectionStatus,
        method: "GET",
      };
      try {
        const { data } = await axiosInstance.request<BackupConnectionStateResponse>(request);
        this.enabled = data.enabled;
      } catch (err) {
        console.error(err);
        this.enabled = false;
      }
    },

    async getConnectors() {
      this.connectorsLoading = true;
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreConnectors(),
      };
      try {
        const { data } = await axiosInstance.request<BackupConnector[]>(request);
        this.connectors = data ?? [];
      } catch (err) {
        console.error(err);
        this.connectors = [];
      } finally {
        this.connectorsLoading = false;
      }
    },

    /**
     * One job page for one direction.
     *
     * Filters are read from the filters store rather than held here: that is where the
     * filter row writes them and where they are persisted across navigation, so the boxes
     * the user sees and the query that is sent cannot drift apart.
     */
    async getJobs(direction: BackupJobDirection) {
      const jobs = this.jobs[direction];
      jobs.loading = true;
      const filters = useFiltersStore().filters[JOB_FILTER_CONTEXTS[direction]];
      const { start, end } = filters.timeFrame;
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreJobs(),
        params: {
          type: direction,
          search: filters.search || undefined,
          status: filters.status ?? undefined,
          // Whole calendar days: the picker yields YYYY-MM-DD, the endpoint wants epoch ms.
          startTime: start ? moment(start).startOf("day").utc().valueOf() : undefined,
          endTime: end ? moment(end).endOf("day").utc().valueOf() : undefined,
          page: jobs.pagination.page,
          pageSize: jobs.pagination.pageSize,
        },
      };
      try {
        const { data } = await axiosInstance.request<BackupJobsResponse>(request);
        jobs.items = data?.items ?? [];
        jobs.total = data?.total ?? 0;
      } catch (err) {
        console.error(err);
        jobs.items = [];
        jobs.total = 0;
      } finally {
        jobs.loading = false;
      }
    },

    /**
     * The Overview doughnuts.
     *
     * The actionboard fetches the same endpoint through its own widget-state machinery;
     * this action keeps the Overview tab independent of that, at the cost of one repeated
     * request for a user who visits both.
     */
    async getDashboard() {
      this.dashboard = { loading: true };
      try {
        const { data } = await axiosInstance.request<BackupAndRestoreWidgetData>({
          ...api.backupAndRestoreDashboard(),
        });
        this.dashboard = { loading: false, data };
      } catch (err) {
        console.error(err);
        this.dashboard = { loading: false, error: true };
      }
    },

    setPagination(direction: BackupJobDirection, pagination: Pagination) {
      this.jobs[direction].pagination = { ...pagination };
    },

    /** Back to the first page, keeping the rows-per-page the user chose. */
    resetPagination(direction: BackupJobDirection) {
      this.jobs[direction].pagination = {
        ...this.jobs[direction].pagination,
        page: defaultCloudBackupState.jobs[direction].pagination.page,
      };
    },
  },
});
